/**
 * Damage packets and the per-element numbers of DESIGN 2.5.
 *
 * `DamageType` lives in status.ts (DESIGN 2.7) and is re-exported here so
 * either import path works.
 */

import type { Element, WovenElement } from './elements.js';
import { ALL_ELEMENTS, isWoven } from './elements.js';
import type { DamageType, StatusId } from './status.js';

export type { DamageType };

/**
 * One cast's contribution per damage type, plus the statuses it applies.
 * `life` is healing on living targets (damage on undead). Statuses are
 * applied in packet order: wet -> chilled -> burning -> shocked -> knockdown
 * (DESIGN 2.7 precedence). An id may repeat for a stacking status: each
 * entry is one application, so a self-cast of three Fire lists `burning`
 * three times and lands three stacks (DESIGN 2.6 "1 stack per Fire").
 */
export interface DamagePacket {
  physical: number;
  arcane: number;
  life: number;
  fire: number;
  cold: number;
  lightning: number;
  steam: number;
  water: number;
  statuses: StatusId[];
  /**
   * Burning stacks this packet sets on its target (the Fire count of the queue).
   * Channels re-apply their packet every damage tick, so burning is set to
   * max(current, burn) and refreshed rather than +1 per tick; that keeps one
   * Fire at one stack and makes five Fires matter. Absent: each 'burning'
   * entry adds one stack (self-cast, bombs, magicks).
   */
  burn?: number;
  /**
   * The part of `physical` that came from Mud. Physical damage has no element
   * of its own, so this is what lets a Mud Ward block mud's damage as well as
   * its statuses (DESIGN 2.5 Aura). Absent: no Mud in the packet.
   */
  mudPhysical?: number;
}

/** Which column of the DESIGN 2.5 table a packet is built from. */
export type PacketForm = 'beam'|'spray'|'arc'|'payload'|'nova';

/** The order statuses inside one packet are applied (DESIGN 2.7 precedence). */
export const STATUS_PRECEDENCE: readonly StatusId[] = ['wet', 'mired', 'chilled', 'frozen', 'burning', 'shocked', 'knockdown', 'hasted', 'slowed', 'blinded'];

const PRECEDENCE_INDEX: Readonly<Record<StatusId, number>> = {
  wet: 0, mired: 1, chilled: 2, frozen: 3, burning: 4, shocked: 5, knockdown: 6, hasted: 7, slowed: 8, blinded: 9,
};

/** Stacking law per element (DESIGN 2.5): s(n), T(n), n^2 or flat. */
type Stacking = 'fluid'|'earth'|'ice'|'flat';

interface ElementRow {
  /** Damage type the numbers go into; null for Shield (never deals damage). */
  type: DamageType | null;
  stacking: Stacking;
  /** Status the element applies whenever it is present in a packet. */
  status: StatusId | null;
  /** Further statuses (mud also wets). */
  extra?: readonly StatusId[];
  beam: number;
  spray: number;
  arc: number;
  payload: number;
  nova: number;
}

/**
 * The DESIGN 2.5 per-element table. Numbers are per tick for beam/spray/arc,
 * per impact for payload, once for nova; each is multiplied by the element's
 * stacking law in `elementPacket`. Life numbers are healing.
 */
export const ELEMENT_NUMBERS: Readonly<Record<Exclude<Element, WovenElement>, ElementRow>> = {
  water:     { type: 'water',     stacking: 'fluid', status: 'wet',       beam: 0,   spray: 0,  arc: 0,  payload: 0,     nova: 0 },
  // DESIGN 2.5 lists the Fire nova as a bare 55; it scales by s(n) here like every other fluid nova.
  fire:      { type: 'fire',      stacking: 'fluid', status: 'burning',   beam: 30,  spray: 15, arc: 0,  payload: 60,    nova: 55 },
  cold:      { type: 'cold',      stacking: 'fluid', status: 'chilled',   beam: 12,  spray: 8,  arc: 0,  payload: 25,    nova: 20 },
  steam:     { type: 'steam',     stacking: 'fluid', status: 'wet',       beam: 90,  spray: 70, arc: 0,  payload: 280,   nova: 140 },
  // Spray lightning deals 0 itself and doubles the whole spray packet (resolve.ts).
  lightning: { type: 'lightning', stacking: 'fluid', status: 'shocked',   beam: 50,  spray: 0,  arc: 31, payload: 40,    nova: 250 },
  arcane:    { type: 'arcane',    stacking: 'flat',  status: null,        beam: 106, spray: 0,  arc: 0,  payload: 425,   nova: 425 },
  life:      { type: 'life',      stacking: 'flat',  status: null,        beam: 89,  spray: 0,  arc: 0,  payload: 360,   nova: 359 },
  // Earth payload is the boulder's direct hit (resolve.ts moves it into `direct`); its nova is the quake (0 damage).
  earth:     { type: 'physical',  stacking: 'earth', status: 'knockdown', beam: 0,   spray: 0,  arc: 0,  payload: 75,    nova: 0 },
  // Ice payload (137.5 n^2) rides a boulder as direct damage; the ice nova scales by s(n) like a fluid.
  ice:       { type: 'physical',  stacking: 'ice',   status: 'chilled',   beam: 0,   spray: 0,  arc: 0,  payload: 137.5, nova: 275 },
  shield:    { type: null,        stacking: 'flat',  status: null,        beam: 0,   spray: 0,  arc: 0,  payload: 0,     nova: 0 },
  // Fusions. Mud: heavy, slowing, soaking crowd control. Lava: an earth boulder full of fire that leaves
  // a burning pool (resolve.ts). Spring: a healing mist (life damage = healing) that also soaks you.
  mud:       { type: 'physical',  stacking: 'fluid', status: 'mired', extra: ['wet'], beam: 20, spray: 12, arc: 0, payload: 60, nova: 60 },
  lava:      { type: 'fire',      stacking: 'earth', status: 'burning', extra: ['knockdown'], beam: 0, spray: 0, arc: 0, payload: 120, nova: 180 },
  spring:    { type: 'life',      stacking: 'fluid', status: 'wet',       beam: 45,  spray: 35, arc: 0,  payload: 180,   nova: 220 },
};

/**
 * A woven piece's numbers (docs/spells-depth.md 1.5): its two parents' numbers in
 * the same form, summed, times `bonus` (1.1 at tier 2, 1.25 at tier 3, 1.4 at
 * tier 4), with `as` turning all of it into one damage type. Its statuses are
 * picked by hand (`status` + `extra`) and replace the parents'. `burn`: the
 * burning stacks it sets. Two of the same piece stack like fluids. `fixed`
 * replaces the sum in a form with a number picked by hand (into `as`): the
 * pieces whose parents' numbers don't say what they mean (Pulse heals, the
 * Void orb, the Glacier's crush, the concepts).
 */
interface WovenRow {
  parents: readonly [Element, Element]; bonus: number; status: StatusId | null; extra?: readonly StatusId[]; as?: DamageType; burn?: number;
  fixed?: Partial<Record<PacketForm, number>>;
}

export const WOVEN_NUMBERS: Readonly<Record<WovenElement, WovenRow>> = {
  plasma:      { parents: ['fire', 'lightning'], bonus: 1.1, status: 'burning', extra: ['shocked'] },
  flux:        { parents: ['lightning', 'arcane'], bonus: 1.1, status: null },
  stillness:   { parents: ['cold', 'shield'], bonus: 1.1, status: 'slowed' },
  // Glass: fire and ice become one sharp physical hit that knocks down; it doesn't burn or chill.
  obsidian:    { parents: ['lava', 'ice'], bonus: 1.25, status: 'knockdown', as: 'physical' },
  ether:       { parents: ['steam', 'arcane'], bonus: 1.25, status: 'wet' },
  cloud:       { parents: ['steam', 'steam'], bonus: 1.25, status: 'wet' },
  ash:         { parents: ['lava', 'steam'], bonus: 1.25, status: 'burning', extra: ['blinded'] },
  rime:        { parents: ['ice', 'cold'], bonus: 1.25, status: 'chilled' },
  moment:      { parents: ['flux', 'stillness'], bonus: 1.25, status: null },
  dragonfire:  { parents: ['plasma', 'lava'], bonus: 1.25, status: 'burning', burn: 2 },
  stormglass:  { parents: ['obsidian', 'lightning'], bonus: 1.4, status: 'shocked', extra: ['knockdown'] },
  thunderhead: { parents: ['cloud', 'lightning'], bonus: 1.4, status: 'wet', extra: ['shocked'] },
  time:        { parents: ['moment', 'moment'], bonus: 1.4, status: 'slowed' },
  // slice 2. Tide and Whirlpool are the sea's weight: they push and soak, they don't hurt.
  tide:        { parents: ['water', 'water'], bonus: 1.1, status: 'wet' },
  // A heartbeat: all of it heals (the living, enemies too; it hurts the undead).
  pulse:       { parents: ['life', 'lightning'], bonus: 1.1, status: null, as: 'life', fixed: { arc: 20, beam: 60, spray: 20, payload: 300, nova: 250 } },
  // The orb is 300 arcane; the unmaking ring hurts no one.
  void:        { parents: ['arcane', 'shield'], bonus: 1.1, status: null, as: 'arcane', fixed: { payload: 300, nova: 0 } },
  wildfire:    { parents: ['fire', 'arcane'], bonus: 1.1, status: 'burning', as: 'fire', burn: 2, fixed: { nova: 150 } },
  whirlpool:   { parents: ['tide', 'flux'], bonus: 1.25, status: 'wet' },
  deluge:      { parents: ['whirlpool', 'cloud'], bonus: 1.4, status: 'wet' },
  inferno:     { parents: ['dragonfire', 'wildfire'], bonus: 1.4, status: 'burning', as: 'fire', burn: 6, fixed: { nova: 800 } },
  // A frozen mountain: one crushing physical hit that knocks down and chills (then freezes, resolve.ts / the server).
  glacier:     { parents: ['rime', 'earth'], bonus: 1.4, status: 'chilled', extra: ['knockdown'], as: 'physical', fixed: { payload: 600, nova: 300 } },
  soul:        { parents: ['ether', 'life'], bonus: 1.4, status: null, as: 'life', fixed: { beam: 120, payload: 400, nova: 600 } },
  // Concepts (tier 5) have fixed numbers.
  sun:         { parents: ['inferno', 'ether'], bonus: 1, status: 'burning', extra: ['blinded'], as: 'fire', burn: 4, fixed: { beam: 150, spray: 60, arc: 60, payload: 1500, nova: 1500 } },
  winter:      { parents: ['glacier', 'thunderhead'], bonus: 1, status: 'chilled', as: 'cold', fixed: { beam: 40, spray: 20, arc: 20, payload: 200, nova: 200 } },
  death:       { parents: ['soul', 'void'], bonus: 1, status: null, as: 'arcane', fixed: { beam: 0, spray: 0, arc: 0, payload: 0, nova: 0 } },
};

/** Water's knockback per Water in the queue, u/s, by form (DESIGN 2.5 Water row). */
export const WATER_KNOCKBACK = { beam: 2, spray: 3, nova: 6 } as const;

/** Lightning in a spray doubles the whole per-tick packet (DESIGN 2.5 "0, whole packet x2"). */
export const SPRAY_LIGHTNING_MUL = 2;

/** All zeros, no statuses. */
export function emptyPacket(): DamagePacket {
  return { physical: 0, arcane: 0, life: 0, fire: 0, cold: 0, lightning: 0, steam: 0, water: 0, statuses: [] };
}

/** Unique statuses in DESIGN 2.7 precedence order. */
export function sortStatuses(ids: readonly StatusId[]): StatusId[] {
  const out: StatusId[] = [];
  for (const id of ids) if (!out.includes(id)) out.push(id);
  return out.sort((a, b) => PRECEDENCE_INDEX[a] - PRECEDENCE_INDEX[b]);
}

/** Component-wise sum; statuses are the union in precedence order. Never mutates. */
export function addPackets(a: DamagePacket, b: DamagePacket): DamagePacket {
  return {
    physical: a.physical + b.physical,
    arcane: a.arcane + b.arcane,
    life: a.life + b.life,
    fire: a.fire + b.fire,
    cold: a.cold + b.cold,
    lightning: a.lightning + b.lightning,
    steam: a.steam + b.steam,
    water: a.water + b.water,
    statuses: sortStatuses([...a.statuses, ...b.statuses]),
    ...(a.burn !== undefined || b.burn !== undefined ? { burn: Math.max(a.burn ?? 0, b.burn ?? 0) } : {}),
    ...(a.mudPhysical !== undefined || b.mudPhysical !== undefined ? { mudPhysical: (a.mudPhysical ?? 0) + (b.mudPhysical ?? 0) } : {}),
  };
}

/** Scale every damage number by k; statuses unchanged. Never mutates. */
export function scalePacket(p: DamagePacket, k: number): DamagePacket {
  return {
    physical: p.physical * k,
    arcane: p.arcane * k,
    life: p.life * k,
    fire: p.fire * k,
    cold: p.cold * k,
    lightning: p.lightning * k,
    steam: p.steam * k,
    water: p.water * k,
    statuses: [...p.statuses],
    ...(p.burn !== undefined ? { burn: p.burn } : {}),
    ...(p.mudPhysical !== undefined ? { mudPhysical: p.mudPhysical * k } : {}),
  };
}

/** Sum of all damage numbers (life counted as positive). */
export function packetTotal(p: DamagePacket): number {
  return p.physical + p.arcane + p.life + p.fire + p.cold + p.lightning + p.steam + p.water;
}

/** The packet's statuses with every number zeroed ("applies X but deals nothing here"). */
export function statusesOnly(p: DamagePacket): DamagePacket {
  return { ...emptyPacket(), statuses: [...p.statuses] };
}

/** s(n) = sqrt(n) for Water/Fire/Cold/Steam/Lightning. */
export function stackFluid(n: number): number {
  return n > 0 ? Math.sqrt(n) : 0;
}

/** T(n) = n(n+1)/2 for Earth. */
export function stackEarth(n: number): number {
  return n > 0 ? (n * (n + 1)) / 2 : 0;
}

/** n^2 for Ice. */
export function stackIce(n: number): number {
  return n > 0 ? n * n : 0;
}

/** The stacking factor for `n` copies of `el` in a form (Ice stacks n^2 only as a boulder payload). */
export function stackFactor(el: Element, form: PacketForm, n: number): number {
  if (n <= 0) return 0;
  if (isWoven(el)) return stackFluid(n);
  switch (ELEMENT_NUMBERS[el].stacking) {
    case 'fluid': return stackFluid(n);
    case 'earth': return stackEarth(n);
    case 'ice': return form === 'payload' ? stackIce(n) : stackFluid(n);
    case 'flat': return 1;
  }
}

/**
 * The DESIGN 2.5 per-element row for `n` copies of `el` in the given form.
 * Fluids use stackFluid(n), Earth stackEarth(n), Ice stackIce(n); Arcane and
 * Life are flat. The element's status rides along whenever n > 0, even in a
 * form where it deals nothing (a Water+Lightning arc wets, then shocks).
 */
export function elementPacket(form: PacketForm, el: Element, n: number): DamagePacket {
  const p = emptyPacket();
  if (n <= 0) return p;
  if (isWoven(el)) return wovenPacket(form, el, n);
  const row = ELEMENT_NUMBERS[el];
  const amount = row[form] * stackFactor(el, form, n);
  if (row.type !== null && amount !== 0) p[row.type] = amount;
  if (el === 'mud' && amount !== 0) p.mudPhysical = amount;
  if (row.status !== null) p.statuses.push(row.status);
  if (row.extra) p.statuses = sortStatuses([...p.statuses, ...row.extra]);
  if (row.status === 'burning') p.burn = n;
  return p;
}

const DAMAGE_KEYS = ['physical', 'arcane', 'life', 'fire', 'cold', 'lightning', 'steam', 'water'] as const;

function wovenPacket(form: PacketForm, el: WovenElement, n: number): DamagePacket {
  const row = WOVEN_NUMBERS[el];
  const p = emptyPacket();
  const fixed = row.fixed?.[form];
  if (fixed !== undefined) {
    p[row.as ?? 'arcane'] = fixed * stackFluid(n);
  } else {
    const k = row.bonus * stackFluid(n);
    const parts = scalePacket(addPackets(elementPacket(form, row.parents[0], 1), elementPacket(form, row.parents[1], 1)), k);
    for (const key of DAMAGE_KEYS) {
      if (row.as !== undefined) p[row.as] += parts[key];
      else p[key] = parts[key];
    }
  }
  const statuses: StatusId[] = row.status !== null ? [row.status] : [];
  p.statuses = sortStatuses([...statuses, ...(row.extra ?? [])]);
  if (p.statuses.includes('burning')) p.burn = row.burn ?? n;
  return p;
}

/** Count of each element in a queue. */
export type ElementCounts = Partial<Record<Element, number>>;

export function countElements(queue: readonly Element[]): ElementCounts {
  const counts: ElementCounts = {};
  for (const el of queue) counts[el] = (counts[el] ?? 0) + 1;
  return counts;
}

/** Sum of `elementPacket(form, el, n)` over every counted element except `skip`. */
export function sumPackets(form: PacketForm, counts: ElementCounts, skip: readonly Element[] = []): DamagePacket {
  let p = emptyPacket();
  for (const el of ALL_ELEMENTS) {
    const n = counts[el] ?? 0;
    if (n > 0 && !skip.includes(el)) p = addPackets(p, elementPacket(form, el, n));
  }
  return p;
}

/**
 * Best-effort list of the elements a packet carries, for HUD names of specs
 * that do not keep their queue (self, melee, walls, mines, storms, quakes).
 * `physicalAs` says what physical damage means for this packet, or null to
 * ignore it (a melee base hit, an ice wall's rise).
 */
export function packetElements(p: DamagePacket, physicalAs: 'earth'|'ice'|null): Element[] {
  const has = (s: StatusId): boolean => p.statuses.includes(s);
  const ice = physicalAs === 'ice' && p.physical > 0;
  const earth = physicalAs === 'earth' && (p.physical > 0 || has('knockdown'));
  const picked = new Set<Element>();
  if (p.water > 0 || (has('wet') && p.steam <= 0)) picked.add('water');
  if (p.life > 0) picked.add('life');
  if (p.cold > 0 || (has('chilled') && !ice)) picked.add('cold');
  if (p.lightning > 0 || has('shocked')) picked.add('lightning');
  if (p.arcane > 0) picked.add('arcane');
  if (earth) picked.add('earth');
  if (p.fire > 0 || has('burning')) picked.add('fire');
  if (p.steam > 0) picked.add('steam');
  if (ice) picked.add('ice');
  return ALL_ELEMENTS.filter((el) => picked.has(el));
}
